/* eslint-disable @next/next/no-img-element */
import React, { useState, useContext, useEffect } from 'react'
import ProductCard from '../../components/ProductCard'
import Context from '../../context'
import SocialShare from '../../components/SocialShare'
import getConfig from 'next/config'
import { InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import { useAuthAPI } from '../../hooks/api.hooks'
import { useAddToCart } from '../../hooks/cart.hooks'
import SVG from '../../components/SVG'
import { writeCookie } from '../../utils/writeCookie'
import { useIonModal } from '@ionic/react'
import { setPageViewData } from '../../utils/analytics'

const { publicRuntimeConfig } = getConfig()

const Collage = ({
  collage,
  fullUrl,
  slug
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { state } = useContext(Context)
  const [total, setTotal] = useState(0)
  const [animated, setAnimated] = useState(false)
  const [collageInfo, setCollageInfo] = useState(collage[0])
  const router = useRouter()
  const authAPICall = useAuthAPI()
  const addToCartCall = useAddToCart()
  let addAllUPCList = ''

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.ptype = 'content'
      window.br_data.user_id = state.user.UID || state.deviceId
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE;

      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID
          }.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
      })()
    }
  }, [])

  useEffect(() => {
    authAPICall(
      'collections',
      'collage',
      'GET',
      {
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        slug,
        popkey: publicRuntimeConfig.POP_KEY
      },
      {}
    ).then((data) => setCollageInfo(data[0]))
  }, [state.cart.store?.storenumber])

  const getTitle = () => {
    return `${collageInfo?.title} - ${collageInfo?.template?.length} product${collageInfo?.template?.length === 1 ? '' : 's'}`
  }

  useEffect(() => {
    setPageViewData(`collage|${collageInfo?.slug}`, 'Other', collageInfo?.slug)
  }, [])

  const handleDismiss = () => {
    dismiss()
  }
  const [present, dismiss] = useIonModal(SocialShare, {
    handleDismiss,
    pageType: 'Collage',
    item: collageInfo,
    image: collageInfo?.main_image,
    title: collageInfo?.title,
    description: `Browse ${collageInfo?.title} items at pOpshelf`
  })

  const addAllClicked = async () => {
    if (!state.cart?.collageInfo?.template?.length) {
      writeCookie('needToConfirmStore', true, 168)
    }
    for (const product of (collageInfo?.template ?? [])) {
      if (product.in_stock && !product.in_store_only) {
        setAnimated(true)
        addToCartCall(state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE, product.upc, product.digital_sku, product.sku, 1, true).then(() => setAnimated(false))
      }
    }
  }

  const openCircle = (element) => {
    document.querySelectorAll('.location-cirle').forEach((foundElement) => {
      foundElement !== element &&
        foundElement.children[0].classList.add('closed')
    })
    if (element.children[0].classList.contains('closed')) {
      element.children[0].classList.remove('closed')
      element.children[0].classList.add('open')
    } else {
      element.children[0].classList.remove('open')
      element.children[0].classList.add('closed')
    }
  }

  const renderCircles = () => {
    const arr = []
    const locations = collageInfo?.template
    let index = 1
    for (const location of locations) {
      const leftCoord = location.coords[0]
      const topCoord = location.coords[1]
      arr.push(
        <div
          className="location-cirle"
          style={{ left: `${leftCoord}%`, top: `${topCoord}%` }}
          onClick={(e) => {
            openCircle(e.target)
          }}
        >
          {index}
          <div
            className="location-cirle-details closed"
            onClick={() => router.push(`/p/${location.slug}`)}
          >
            <label>{location.product_name}</label>
            <span>${parseFloat(location.price).toFixed(2)}</span>
          </div>
        </div>
      )
      index++
    }
    return arr
  }

  const renderProducts = () => {
    let totalPrice = 0
    const arr = collageInfo?.template?.map((product, index) => {
      if (product.in_stock && !product.in_store_only) {
        if (addAllUPCList && addAllUPCList.length > 0) {
          addAllUPCList += ','
        }
        addAllUPCList += product.upc
      }
      if (product.in_stock && !product.in_store_only && parseFloat(product.final_price || 0)?.toFixed(2) !== '0.01') { totalPrice += parseFloat(product.final_price) }
      return (
        <div key={product.slug} className="collage__products-card">
          <ProductCard data={product} vertical={true} forceVertical={true} number={product.index + 1} />
        </div>
      )
    })
    totalPrice !== total && setTotal(totalPrice)
    return arr
  }

  const renderButtons = () => {
    return (
      <button
        id="collage-add-all"
        data-price={total.toFixed(2)}
        data-upc={addAllUPCList}
        onClick={addAllClicked}
        aria-label="Add all to cart"
        disabled={
          !collageInfo?.template?.some(
            (product) =>
              product.in_stock && !product.in_store_only && parseFloat(product.final_price) > 0.01
          )
        }
        className={`btn__primary ${!animated && 'unchecked_all_cart'} ${animated && 'check_cart'}`}
      />
    )
  }

  /* added dynamically page title and meta tag */
  useEffect(() => {
    document.title = collageInfo.title

    const metaTag = document.createElement('meta')
    metaTag.name = 'description'
    metaTag.content = `Browse ${collageInfo.title} items at pOpshelf`

    const metaProperties = [
      { property: 'og:url', content: fullUrl },
      { property: 'og:type', content: 'collage' },
      {
        property: 'og:title',
        content: Object.keys(collage).length !== 0 && getTitle()
      },
      {
        property: 'og:description',
        content: Object.keys(collage).length !== 0 && getTitle()
      },
      {
        property: 'og:image',
        content: collage.length && collageInfo.main_image
      }
    ]

    metaProperties.forEach(({ property, content }) => {
      const metaPropertyTag = document.createElement('meta')
      metaPropertyTag.setAttribute('property', property)
      metaPropertyTag.content = content
      document.head.appendChild(metaPropertyTag)
    })

    document.head.appendChild(metaTag)
  }, [])

  return (
    <div className="collage">
      <section className="collage__wrapper">
        <div className="collage__image">
          <div className="collage__image-wrapper">
          {Object.keys(collage).length !== 0 && renderCircles()}
            <img src={collageInfo.main_image} alt={collageInfo.title} />
          </div>
        </div>

        <div className="collage__products" id={getTitle()}>
          <h1>{Object.keys(collage).length !== 0 && getTitle()}</h1>
          <div className="collage__products-details">
            <p className="collage__products-details-total">Total: <span>${total.toFixed(2)}</span></p>
            <button className="share" onClick={() => present()} aria-label="Open share modal">
              <SVG name="share" label="share" size ="md"/>
            </button>
          </div>

          {Object.keys(collage).length !== 0 && renderProducts()}
          {renderButtons()}
        </div>
      </section>
    </div>
  )
}

export default Collage

export const getServerSideProps = async ({ query, req }) => {
  const { publicRuntimeConfig } = getConfig()
  let fullUrl
  if (req) {
    fullUrl = req.headers.host + req.url
  } else {
    fullUrl =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '')
  }

  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/collections/${publicRuntimeConfig.ENV}/collage?store=${publicRuntimeConfig.DEFAULT_STORE}&slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}`
  )
  const collage = await res.json()

  return {
    props: {
      collage,
      fullUrl,
      slug: query.slug
    }
  }
}
